import { useRef, useCallback } from 'react';

/**
 * Custom hook for handling scroll-to-element functionality
 * @returns {Object} Scroll functionality and ref
 */
export function useScrollToElement() {
  const contentRef = useRef(null);

  /**
   * Scrolls an element into the middle of the panel
   * @param {HTMLElement} element - The element to scroll into view
   */
  const scrollToMiddle = useCallback(
    /** @param {HTMLElement} element */
    (element) => {
      if (!element) {
        return;
      }

      try {
        // Get the scrollable container (giftWrap__content)
        /** @type {HTMLElement | null} */
        const container = contentRef.current;
        if (!container) {
          // Fallback to scrollIntoView
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          return;
        }

        // Type assertion after null check
        /** @type {HTMLElement} */
        const containerElement = container;

        // Check if element is already fully visible in the container
        /** @type {DOMRect} */
        const elementRect = element.getBoundingClientRect();
        /** @type {DOMRect} */
        const containerRect = containerElement.getBoundingClientRect();

        // Calculate if element is fully visible within the container
        const isFullyVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

        // Only scroll if element is not fully visible
        if (isFullyVisible) {
          return;
        }

        // Calculate the scroll position to center the element in the container
        /** @type {number} */
        const containerScrollTop = containerElement.scrollTop;
        /** @type {number} */
        const containerHeight = containerElement.clientHeight;
        /** @type {number} */
        const elementHeight = element.offsetHeight;

        // Calculate the element's position relative to the container's scroll position
        const elementRelativeTop = elementRect.top - containerRect.top + containerScrollTop;

        // Calculate the target scroll position to center the element
        const targetScrollTop = elementRelativeTop - containerHeight / 2 + elementHeight / 2;

        // Smooth scroll to the calculated position
        if (containerElement && typeof containerElement.scrollTo === 'function') {
          containerElement.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        }
      } catch (error) {
        console.warn('Scroll to middle failed:', error);
      }
    },
    [contentRef]
  );

  /**
   * Scrolls an element into view
   * @param {HTMLElement} element - The element to scroll into view
   */
  const scrollToElement = useCallback(
    (element) => {
      scrollToMiddle(element);
    },
    [scrollToMiddle]
  );

  return {
    contentRef,
    scrollToElement
  };
}
